import React from 'react';
import _ from 'lodash';
import { Card, CardContent, CardHeader, Button, Stack, TextField, List, ListItem, ListItemText, IconButton, Portal, Modal, Divider, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useParameterizedQuery } from 'react-fetching-library';
import { useThrottle } from '../../utils/hooks';

import { getOrdersAction } from '../../services/request';
import { calculateDebt } from '../../services/functions';
import { useSettings, useResponsive } from '../../services/hooks';
import Page from '../../components-core/Page';
import { OrderList, OrderSidebar, OrderDetails } from '../../sections/employee/order';
import { setAllowAutoOrderUpdate } from '../../services/redux-actions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import { SnackbarAlert } from '../../components-core/SnackbarAlert';

import AddPaymentModal from '../../sections/employee/order/order-details/tabs/OrderTab/AddPaymentModal';
import ChangeStateModal from '../../sections/employee/order/context-menu/ChangeStateModal';

const OrderDesktop = () => {
  const dispatch = useDispatch();
  const { allowAutoOrderUpdate, module } = useSelector(state => state.app);
  const [pageSettings, setPageSettings] = React.useState({ page: 1, pageSize: 100, search: null, sortingFields: [] });
  const { loading, payload, error, query } = useQuery(getOrdersAction(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields));
  const [orders, setOrders] = React.useState([]);
  const { themeStretch } = useSettings();
  const [openSidebar, setOpenSidebar] = React.useState(false);
  const [openCompose, setOpenCompose] = React.useState(false);
  const [hideSidebar, setHideSidebar] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const [idList, setIdList] = React.useState([]);
  const [ordersDataForMessages, setOrdersDataForMessages] = React.useState([]);

  React.useEffect(() => {
    setPageSettings(prev => ({ ...prev, page: 1 }));
  }, [module]);

  React.useEffect(() => {
    if (!error && !loading) {
      setIdList(payload.id_list);
      setOrdersDataForMessages(payload.orders_data_for_messages);
      setCount(payload.count);
      setOrders(payload.results);
    }
  }, [loading]);

  React.useEffect(() => {
    const updateOrdersInterval = setInterval(() => {
      if (allowAutoOrderUpdate) {
        query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields);
      }
    }, 30 * 1000);

    return () => clearInterval(updateOrdersInterval);
  }, [query, allowAutoOrderUpdate]);

  const queryCallback = (pageD, pageSize, search = null, sortingFields) => {
    const page = pageD + 1;
    setPageSettings({ page, pageSize, search, sortingFields });
    query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields);
  };

  const updateOrders = newOrder => setOrders(prev => prev.map(order => order.id === newOrder.id ? newOrder : order));
  const addOrder = newOrder => setOrders(prev => [newOrder, ...prev]);
  const removeOrderFromOrders = orderId => setOrders(prev => prev.filter(order => order.id !== orderId));

  return (
    <Page title="Zapisy">
      <Card sx={{ height: { md: '72vh' }, display: { md: 'flex' } }}>
        { !hideSidebar && (
          <OrderSidebar
            isOpenSidebar={openSidebar}
            onCloseSidebar={() => setOpenSidebar(false)}
            onOpenCompose={() => setOpenCompose(true)}
            idList={idList}
            setIdList={setIdList}
            ordersDataForMessages={ordersDataForMessages}
            refreshOrders={() => query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields)}
          />)
        }
        <OrderList
          hideSidebar={() => setHideSidebar(!hideSidebar)}
          onOpenSidebar={() => setOpenSidebar(true)}
          count={count}
          queryCallback={queryCallback}
          loading={loading}
          orders={orders}
          updateOrders={updateOrders}
          addOrder={addOrder}
          removeOrderFromOrders={removeOrderFromOrders}
          refreshOrders={() => query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields)}
        />
        <OrderDetails
          isOpenCompose={openCompose}
          onCloseCompose={() => setOpenCompose(false)}
          updateOrders={updateOrders}
          removeOrderFromOrders={removeOrderFromOrders}
        />
      </Card>
    </Page>
  );
};


const OrderMobile = () => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  };

  const [text, setText] = React.useState('');
  const throttledText = useThrottle(text);
  const [open, setOpen] = React.useState(false);
  const [openStatusModal, setOpenStatusModal] = React.useState(false);
  const [order, setOrder] = React.useState({});
  const [orders, setOrders] = React.useState([]);
  const [snackbar, setSnackbar] = React.useState({ open: false, severity: 'error', message: 'Wystąpił błąd' });
  const [pageSettings, setPageSettings] = React.useState({ page: 1, pageSize: 100, search: null, sortingFields: ['-debt'] });
  const { loading, payload, error, query } = useQuery(getOrdersAction(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields));
  // const { query } = useParameterizedQuery(getOrdersAction);

  React.useEffect(() => {
    if (!error && !loading) {
      setOrders(payload.results);
    }
  }, [loading]);

  React.useEffect(async () => {
    await handleSearch();
  }, [throttledText]);

  const handleTextChange = e => setText(e.target.value);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenSnackbar = (severity, message) => setSnackbar({ open: true, severity, message });


  const handleOpen = order => {
    setOrder(order);
    setOpen(true);
  };

  const handleClose = () => {
    // setOrder({});
    setOpen(false);
  };

  const handleOpenStatusModal = order => {
    setOrder(order);
    setOpenStatusModal(true);
  };

  const handleSearch = async () => {
    if (text.trim().length > 0) {
      setPageSettings(prev => ({ ...prev, page: 1, pageSize: 100, search: text }));

      const { error, payload } = await query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields);

      if (!error) {
        setOrders(payload.results);
      }

    } else {
      setPageSettings(prev => ({ ...prev, page: 1, pageSize: 100, search: null }));

      const { error, payload } = await query(pageSettings.page, pageSettings.pageSize, pageSettings.search, pageSettings.sortingFields);

      if (!error) {
        setOrders(payload.results);
      }
    }
  };

  const handleClear = () => {
    setText('');
  };

  const updateOrder = updatedOrder => {
    setOrder(updatedOrder);
    setOrders(prev => prev.map(order => order.id === updatedOrder.id ? updatedOrder : order));
  };

  return (<Page title="Zamówienia">
    <Stack justifyContent="center" alignItems="center" p={2}>
      <TextField
        fullWidth
        label="Wyszukaj zamówienie"
        value={text}
        onChange={handleTextChange}
      ></TextField>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSearch}>Szukaj</Button>
        <Button variant="contained" onClick={handleClear}>Wyczyść</Button>
      </Stack>
    </Stack>
    <List>
      {loading
        ? <>
          <Skeleton variant="rectangular" height={120} sx={{ m: 2 }}/>
          <Skeleton variant="rectangular" height={120} sx={{ m: 2 }}/>
          <Skeleton variant="rectangular" height={120} sx={{ m: 2 }}/>
          <Skeleton variant="rectangular" height={120} sx={{ m: 2 }}/>
        </>
        : null}

      {orders.map(order => <React.Fragment key={order.id}>
        <ListItem
          style={{ paddingRight: '96px' }}
          secondaryAction={
            <>
              <IconButton color="primary" onClick={() => handleOpenStatusModal(order)}>
                <EditIcon />
              </IconButton>
              <IconButton color="primary" onClick={() => handleOpen(order)}>
                <AttachMoneyIcon />
              </IconButton>
            </>
          }
          sx={{ mb: 2 }}
        >
          <ListItemText
            style={{ whiteSpace: 'pre-line' }}
            primary={order.participant_name}
            secondary={`${order.event_name}\nStatus: ${order.state_name}\nZadłużenie:  ${calculateDebt(order.order_payments)}`}
          />
        </ListItem>
        <Divider />
      </ React.Fragment>)}
    </List>
    <Portal>
      <Modal open={open} onClose={handleClose}>
        <Card sx={{ ...style, width: '90vw' }}>
          <CardHeader
            title="Dodaj płatność"
          />
          <CardContent sx={{ textAlign: 'center' }}>
            <AddPaymentModal order={order} handleClose={handleClose} handleOpenSnackbar={handleOpenSnackbar} updateOrderList={updateOrder}/>
          </CardContent>
        </Card>
      </Modal>
    </Portal>
    <Portal>
      {openStatusModal ? <ChangeStateModal order={order} open={openStatusModal} handleClose={() => setOpenStatusModal(false)} setOrders={setOrders}/> : null}
    </Portal>
    <Portal>
      <SnackbarAlert open={snackbar.open} severity={snackbar.severity} message={snackbar.message} onClose={handleCloseSnackbar} />
    </Portal>
  </Page>);
};


export const Order = () => {
  const isDesktop = useResponsive('up', 'lg');
  const [isLandscape, setIsLandscape] = React.useState(false);

  React.useEffect(() => {
    setIsLandscape(screen.orientation.type.startsWith('landscape'));

    const listener = e => {
      setIsLandscape(screen.orientation.type.startsWith('landscape'));
    };

    screen.orientation.addEventListener('change', listener);

    return () => {
      screen.orientation.removeEventListener('change', listener);
    };
  }, []);

  if (isDesktop || isLandscape) {
    return <OrderDesktop />;
  }

  return <OrderMobile />;

};
